import { createColumnHelper } from '@tanstack/react-table';
import React, { useMemo, useState } from 'react';

import { FeatureFlag, GUID, PersonOrg } from '@ndvr/auth-api';

import { useInternalUserProfilesQuery } from '@/modules/internal-user-queries';
import { themeDefault } from 'styles';
import { getPersonName } from 'utils';
import { useFeatureFlag } from 'views/auth/useFeatureFlag';
import { internalUserToCoreData } from 'views/person/person-queries';
import { Button, Card, CondensedTable, IconCog, PageSpinner, Spinner, Text, VerticalLayout } from 'widgets';

import { useFeatureFlagOverrides, useFeatureFlags } from '../dev-queries';
import { useAdminPersonCoreData } from './feature-flag-person-queries';
import { FlagDrawer } from './FlagDrawer';

export interface FeatureFlagWithOverrides {
    featureFlag: FeatureFlag;
    globalOverride?: boolean;
    overrides: GUID<PersonOrg>[];
}
const columnHelper = createColumnHelper<FeatureFlagWithOverrides>();

export const FeatureFlagList = () => {
    const { data: featureFlagOverrides = [], isPending: featureFlagOverridesLoading } = useFeatureFlagOverrides();
    const { data: appUsers } = useAdminPersonCoreData();
    const { data: internalProfiles } = useInternalUserProfilesQuery();
    const internalUsers = useMemo(() => internalProfiles?.map(internalUserToCoreData), [internalProfiles]);
    const { data: featureFlagsRaw, isPending: featureFlagsRawLoading } = useFeatureFlags();
    const [flagToEdit, setFlagToEdit] = useState<FeatureFlagWithOverrides | undefined>();
    const isTestFlagEnabled = useFeatureFlag(FeatureFlag.CC_FEATURE_FLAG_TEST);
    const isLoading = featureFlagOverridesLoading || featureFlagsRawLoading;

    const featureFlags = useMemo(() => {
        if (isLoading || !featureFlagsRaw) {
            return [];
        }

        return featureFlagsRaw.map((flag) => {
            // the featureFlagOverrides array contains _both_ overrides for a PersonOrg and global overrides
            // globalOverride is the entry without a personOrgGUID
            const matchingOverrides = featureFlagOverrides.filter((o) => o.featureFlag === flag);
            const overrides = matchingOverrides.filter((e) => e.personOrgGUID).map((e) => e.personOrgGUID) as string[];

            return {
                featureFlag: flag,
                globalOverride: overrides.length !== matchingOverrides.length,
                overrides,
            };
        });
    }, [featureFlagOverrides, featureFlagsRaw, isLoading]);

    if (isLoading) {
        return <PageSpinner />;
    }

    const columns = [
        columnHelper.accessor('featureFlag', {
            id: 'featureFlag',
            header: 'Flag',
            cell: ({ getValue }) => <Text>{String(getValue())}</Text>,
            size: 250,
        }),
        columnHelper.accessor('globalOverride', {
            id: 'globalOverride',
            header: 'Global Override',
            cell: ({ getValue }) => <Text>{getValue() ? <b>True</b> : 'False'}</Text>,
            size: 105,
        }),
        columnHelper.accessor('overrides', {
            id: 'overrides',
            header: 'Overrides',
            cell: ({ getValue }) => {
                if (!appUsers || !internalUsers) {
                    return <Spinner />;
                }
                const clientNames: string[] = [];
                const internalNames: string[] = [];
                const unknownGUIDs: string[] = [];
                for (const guid of getValue() ?? []) {
                    const appProfile = appUsers.find((user) => user.personOrgGUID === guid);
                    if (appProfile) {
                        clientNames.push(getPersonName(appProfile));
                        continue;
                    }
                    const internalProfile = internalUsers.find((user) => user.personOrgGUID === guid);
                    if (internalProfile) {
                        internalNames.push(getPersonName(internalProfile));
                        continue;
                    }
                    unknownGUIDs.push(guid);
                }
                return (
                    <div className="flex flex-col gap-2">
                        {internalNames.length > 0 && (
                            <div className="flex flex-col">
                                <Text helper $themeColor="subtle">
                                    Internal
                                </Text>
                                <Text>{internalNames.join(', ')}</Text>
                            </div>
                        )}
                        {clientNames.length > 0 && (
                            <div className="flex flex-col">
                                <Text helper $themeColor="subtle">
                                    Client
                                </Text>
                                <Text>{clientNames.join(', ')}</Text>
                            </div>
                        )}
                        {unknownGUIDs.length > 0 && (
                            <div className="flex flex-col">
                                <Text helper $themeColor="subtle">
                                    Unknown
                                </Text>
                                <Text>{unknownGUIDs.join(', ')}</Text>
                            </div>
                        )}
                    </div>
                );
            },
        }),
        columnHelper.display({
            id: 'edit',
            cell: ({ row }) => (
                <Button
                    className="cursor-pointer"
                    size="icon"
                    buttonType="ghost"
                    icon={<IconCog size="xSmall" color={themeDefault.text.subtle} />}
                    onClick={() => {
                        setFlagToEdit(row.original);
                    }}
                />
            ),
            size: 60,
        }),
    ];

    return (
        <VerticalLayout $gap={16}>
            {isTestFlagEnabled && <Text>CC_FEATURE_FLAG_TEST test works!</Text>}
            <Card $padding={0}>
                <CondensedTable columns={columns} data={featureFlags} isLoading={isLoading} />
            </Card>
            {flagToEdit && <FlagDrawer onClose={() => setFlagToEdit(undefined)} flagData={flagToEdit} />}
        </VerticalLayout>
    );
};
